import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {fetchOverviewData} from '../store/slices/overviewSlice';
import {AppDispatch, RootState} from '../store/store';
import {Link} from 'react-router-dom';
import TrialLock from '../components/TrialLock';
import '../styles/OverviewPage.scss';
import {fetchBillingForecast} from "../store/slices/billingSlice";
import {FaExclamationTriangle, FaServer, FaHdd} from 'react-icons/fa';

const OverviewPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {data: instances, status: instancesStatus, error: instancesError} = useSelector(
        (state: RootState) => state.instances
    );
    const {data: overviewData, status: overviewStatus, error: overviewError} = useSelector(
        (state: RootState) => state.overview
    );

    const billing = useSelector((state: RootState) => state.billingForecast);
    // The free tier note can be closed (Milos, 2026-11-02); it stays closed on this browser, like the credit banner.
    const [freeSummaryClosed, setFreeSummaryClosed] = useState<boolean>(() => {
        try { return localStorage.getItem('free-tier-summary-dismissed') === '1'; } catch { return false; }
    });
    const closeFreeSummary = () => {
        try { localStorage.setItem('free-tier-summary-dismissed', '1'); } catch { /* still closes for this visit */ }
        setFreeSummaryClosed(true);
    };
    const freeSummary = billing.forecast?.policy_version === 3 && !freeSummaryClosed ? <div className="overview-free-summary" role="note">
        <p>Your plan includes one free VM and one private PostgreSQL VM. <Link to="/mcp">Create them through MCP</Link>.</p>
        <button type="button" className="overview-free-summary__close" onClick={closeFreeSummary} aria-label="Dismiss" title="Dismiss">×</button>
    </div> : null;

    // Credit-expiry banner is dismissible; keyed on the expiry date so a NEW expiry
    // (new credits) re-shows it. Persisted in localStorage so it stays hidden on reload.
    const [dismissedExpiry, setDismissedExpiry] = useState<string | null>(
        () => localStorage.getItem('credit-expiry-dismissed')
    );

    const isLoading = instancesStatus === 'loading' || overviewStatus === 'loading';

    const runningInstanceCount = instances.filter((i: { status: string }) => i.status === 'ACTIVE').length;

    useEffect(() => {
        if (overviewStatus === 'idle') {
            dispatch(fetchOverviewData());
        }
        if (billing.status === 'idle') {
            dispatch(fetchBillingForecast());
        }
    }, [instances, overviewStatus, billing.status, dispatch]);
    useEffect(() => {
        if (billing.status === 'idle') {
            dispatch(fetchBillingForecast());
        }
    }, [dispatch, billing.status]);

    const dismissCreditExpiryBanner = () => {
        const val = String(billing.forecast?.credits_expires_at ?? '');
        localStorage.setItem('credit-expiry-dismissed', val);
        setDismissedExpiry(val);
    };

    const renderCreditExpiryBanner = () => {
        // No-card trial users already see the TrialBanner (credits + expiry + connect card); this
        // extra banner is redundant/noisy for them.
        if (billing.forecast?.tier === 'trial_no_card') return null;
        if (!billing.forecast?.credits_expiry_soon) return null;
        const expiresAt = billing.forecast.credits_expires_at;
        // Dismissed for this exact expiry date? Stay hidden. A different/new expiry re-shows it.
        if (dismissedExpiry !== null && dismissedExpiry === String(expiresAt ?? '')) return null;
        const daysUntilExpiry = expiresAt
            ? Math.ceil((expiresAt * 1000 - Date.now()) / (1000 * 60 * 60 * 24))
            : null;
        const expiryLabel = expiresAt
            ? new Date(expiresAt * 1000).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})
            : null;

        return (
            <div className="credit-expiry-banner">
                <FaExclamationTriangle size={20} className="credit-expiry-banner__icon" />
                <span>
                    Your account credits expire{' '}
                    {daysUntilExpiry !== null && daysUntilExpiry >= 0
                        ? <>in <strong>{daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</strong></>
                        : 'soon'
                    }
                    {expiryLabel && <> ({expiryLabel})</>}.{' '}
                    Unused credits will be forfeited.{' '}
                    <Link to="/category/user/billing" className="credit-expiry-banner__link">View billing</Link>
                </span>
                <button
                    type="button"
                    className="credit-expiry-banner__close"
                    onClick={dismissCreditExpiryBanner}
                    aria-label="Dismiss"
                    title="Dismiss"
                >×</button>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="fancyspinner">
                    <div className="ring"></div>
                    <div className="ringouter"></div>
                    <div className="dot"></div>
                </div>
            </div>
        );
    }

    if (instancesError || overviewError) {
        return <div className="error-container">Error: {instancesError || overviewError}</div>;
    }

    if (
        instances.length === 0 &&
        overviewData?.totalInstances === 0 &&
        (overviewData.totalVolumesCount ?? 0) === 0 &&
        overviewData.totalSnapshots === 0
    ) {
        return (
            <div className="overview-page">
                {renderCreditExpiryBanner()}
                {freeSummary}
                <div className="hero-section hero-section--console">
                    <div className="hero-content">
                        <span className="overview-kicker">Compute</span>
                        <h1 className="hero-title">On-Demand Virtual Machines</h1>
                        <p className="hero-subtitle">
                            Start your cloud journey with powerful, scalable virtual machines. No upfront commitment.
                        </p>
                        <div className="hero-buttons">
                            <TrialLock locked={false} paidResource><Link to="/category/compute/instances/create" className="btn btn-primary">
                                Create Instance
                            </Link></TrialLock>
                            <a
                                href="https://docs.redu.cloud"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                            >
                                Read Documentation
                            </a>
                        </div>
                    </div>
                    <div className="hero-summary-panel" aria-label="Compute status">
                        <span className="overview-kicker">Ready</span>
                        <strong>Launch your first instance</strong>
                        <p>Compute, storage, and networking controls are ready when you are.</p>
                        <div className="hero-summary-grid">
                            <div>
                                <span>Instances</span>
                                <b>0</b>
                            </div>
                            <div>
                                <span>Forecast</span>
                                <b>£0.00</b>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="docs-section">
                    <h2 className="section-title">Get Started with redu.cloud</h2>
                    <div className="docs-grid">
                        <div className="docs-card">
                            <h3>Getting Started</h3>
                            <p>Learn how to launch your first virtual machine and configure your cloud environment.</p>
                            <a href="https://docs.redu.cloud/#getting-started" target="_blank" rel="noopener noreferrer"
                               className="docs-link">
                                Get Started →
                            </a>
                        </div>
                        <div className="docs-card">
                            <h3>Compute</h3>
                            <p>Explore concepts, usage, and best practices for running your workloads.</p>
                            <a href="https://docs.redu.cloud/Services/compute" target="_blank"
                               rel="noopener noreferrer" className="docs-link">
                                Learn More →
                            </a>
                        </div>
                        <div className="docs-card">
                            <h3>Networking & Security</h3>
                            <p>Set up secure networks, firewalls, and access controls for your infrastructure.</p>
                            <a href="https://docs.redu.cloud/Services/networking" target="_blank" rel="noopener noreferrer"
                               className="docs-link">
                                Explore →
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (overviewData) {
        const hasResources =
            overviewData.totalInstances > 0 ||
            (overviewData.totalVolumesCount ?? 0) > 0 ||
            overviewData.totalSnapshots > 0;
        const overviewMetrics = [
            {title: "Instances", value: overviewData.totalInstances, icon: "🖥️"},
            {title: "vCPUs", value: overviewData.totalVCPUs, icon: "⚡"},
            {title: "RAM", value: `${overviewData.totalRAM} MB`, icon: "🧠"},
            {title: "Storage (Boot)", value: `${overviewData.totalStorage} GB`, icon: "💾"},
            {title: "Volumes", value: `${overviewData.totalVolumes} GB`, icon: "🗄️"},
            {title: "Snapshots", value: overviewData.totalSnapshots, icon: "📸"},
        ];

        return (
            <div className="overview-page">
                {renderCreditExpiryBanner()}
                {freeSummary}
                <div className="hero-section hero-section--console">
                    <div className="hero-content">
                        <span className="overview-kicker">Compute</span>
                        <h1 className="hero-title">Compute overview</h1>
                        <p className="hero-subtitle">
                            Manage your cloud resources and monitor your infrastructure at a glance.
                        </p>
                        <div className="hero-buttons">
                            <TrialLock locked={false} paidResource><Link to="/category/compute/instances/create" className="btn btn-primary">
                                Create New Instance
                            </Link></TrialLock>
                            <a
                                href="https://docs.redu.cloud"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                            >
                                View Documentation
                            </a>
                        </div>
                    </div>
                    <div className="hero-summary-panel" aria-label="Compute status">
                        <span className="overview-kicker">Status</span>
                        <strong>{runningInstanceCount} running</strong>
                        <p>{overviewData.totalInstances} instance{overviewData.totalInstances === 1 ? '' : 's'} across this account.</p>
                        <div className="hero-summary-grid">
                            <div>
                                <span>vCPUs</span>
                                <b>{overviewData.totalVCPUs}</b>
                            </div>
                            <div>
                                <span>Volumes</span>
                                <b>{overviewData.totalVolumesCount ?? 0}</b>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="metrics-section">
                    <h2 className="section-title">Your Cloud Overview</h2>

                    <div className="count-widgets-row">
                        <Link to="/category/compute/instances" className="count-widget">
                            <div className="count-widget__icon">
                                <FaServer />
                            </div>
                            <div className="count-widget__body">
                                <span className="count-widget__number">{overviewData.totalInstances}</span>
                                <span className="count-widget__label">Instances</span>
                                <span className="count-widget__sub">
                                    {runningInstanceCount} running
                                </span>
                            </div>
                            <span className="count-widget__arrow">→</span>
                        </Link>

                        <Link to="/category/block-storage/volumes" className="count-widget">
                            <div className="count-widget__icon">
                                <FaHdd />
                            </div>
                            <div className="count-widget__body">
                                <span className="count-widget__number">{overviewData.totalVolumesCount ?? 0}</span>
                                <span className="count-widget__label">Volumes</span>
                                <span className="count-widget__sub">
                                    {overviewData.totalVolumes} GB total
                                </span>
                            </div>
                            <span className="count-widget__arrow">→</span>
                        </Link>
                    </div>

                    <div className="metrics-grid">
                        {overviewMetrics.map((metric, index) => (
                            <div key={index} className="metric-card">
                                {/*<div className="metric-icon">{metric.icon}</div>*/}
                                <div className="metric-content">
                                    <h3 className="metric-title">{metric.title}</h3>
                                    <p className="metric-value">{metric.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {!hasResources && (
                    <div className="docs-section">
                        <h2 className="section-title">Get Started with redu.cloud</h2>
                        <div className="docs-grid">
                            <div className="docs-card">
                                <h3>Getting Started</h3>
                                <p>Learn how to launch your first virtual machine and configure your cloud environment.</p>
                                <a href="https://docs.redu.cloud/#getting-started" target="_blank" rel="noopener noreferrer"
                                   className="docs-link">
                                    Get Started →
                                </a>
                            </div>
                            <div className="docs-card">
                                <h3>Compute</h3>
                                <p>Explore concepts, usage, and best practices for running your workloads.</p>
                                <a href="https://docs.redu.cloud/Services/compute" target="_blank"
                                   rel="noopener noreferrer" className="docs-link">
                                    Learn More →
                                </a>
                            </div>
                            <div className="docs-card">
                                <h3>Networking & Security</h3>
                                <p>Set up secure networks, firewalls, and access controls for your infrastructure.</p>
                                <a href="https://docs.redu.cloud/Services/networking" target="_blank" rel="noopener noreferrer"
                                   className="docs-link">
                                    Explore →
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default OverviewPage;
